import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { HRService } from '@/services/hr.service';
import { Employee, EmployeeStatus, Department } from '@/models/hr.model';
import { Button, FormInput, FormSelect, FormTextarea, LoadingSpinner, Card } from '@/components/common';
import { Users, User, Mail, Phone, Building2, Briefcase, Calendar, DollarSign, Clock, ArrowLeft, Save } from 'lucide-react';
export const EmployeeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<Employee>>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    department: Department.operations,
    role: '',
    status: EmployeeStatus.pending,
    employmentType: 'full_time',
    startDate: new Date(),
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => id ? HRService.getEmployeeById(id) : null,
    enabled: isEditing && !!id,
  });

  React.useEffect(() => {
    if (employee) {
      setFormData(employee);
    }
  }, [employee]);

  const mutation = useMutation({
    mutationFn: async (data: Partial<Employee>) => {
      if (isEditing && id) {
        await HRService.updateEmployee(id, data);
      } else {
        await HRService.createEmployee(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      navigate('/hr');
    },
    onError: (error: any) => {
      setErrorMessage(error?.message || 'Failed to save employee');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.email?.trim() || !formData.firstName?.trim() || !formData.lastName?.trim()) {
      setErrorMessage('Email, First Name, and Last Name are required');
      return;
    }

    mutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner text="Loading employee..." />
      </div>
    );
  }

  const departmentOptions = Object.values(Department).map(dept => ({
    value: dept,
    label: dept.charAt(0).toUpperCase() + dept.slice(1).replace('_', ' '),
  }));

  const statusOptions = Object.values(EmployeeStatus).map(status => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '),
  }));

  const employmentTypeOptions = [
    { value: 'full_time', label: 'Full Time' },
    { value: 'part_time', label: 'Part Time' },
    { value: 'contractor', label: 'Contractor' },
    { value: 'intern', label: 'Intern' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/hr')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8 text-green-600" />
            {isEditing ? 'Edit Employee' : 'New Employee'}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
          </p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {errorMessage && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{errorMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="First Name"
                  icon={User}
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Enter first name"
                />

                <FormInput
                  label="Last Name"
                  icon={User}
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Enter last name"
                />

                <FormInput
                  label="Email"
                  type="email"
                  icon={Mail}
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="employee@company.com"
                />

                <FormInput
                  label="Phone Number"
                  type="tel"
                  icon={Phone}
                  value={formData.phoneNumber || ''}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-green-600" />
                Employment Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormSelect
                  label="Department"
                  icon={Building2}
                  required
                  options={departmentOptions}
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value as Department })}
                />

                <FormInput
                  label="Role/Title"
                  icon={Briefcase}
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Software Engineer"
                />

                <FormSelect
                  label="Status"
                  icon={Users}
                  required
                  options={statusOptions}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as EmployeeStatus })}
                />

                <FormSelect
                  label="Employment Type"
                  icon={Clock}
                  required
                  options={employmentTypeOptions}
                  value={formData.employmentType}
                  onChange={(e) => setFormData({ ...formData, employmentType: e.target.value as any })}
                />

                <FormInput
                  label="Start Date"
                  type="date"
                  icon={Calendar}
                  required
                  value={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
                />

                <FormInput
                  label="Salary (Annual)"
                  type="number"
                  step="0.01"
                  icon={DollarSign}
                  value={formData.salary || ''}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="0.00"
                />

                <FormInput
                  label="Hourly Rate"
                  type="number"
                  step="0.01"
                  icon={DollarSign}
                  value={formData.hourlyRate || ''}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => navigate('/hr')}
              icon={ArrowLeft}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              icon={Save}
              isLoading={mutation.isPending}
              iconPosition="right"
            >
              {isEditing ? 'Update Employee' : 'Create Employee'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

